import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveDraftComposition } from '../lib/saveDraft';
import { clearHistory } from '../store/slices/undoRedoSlice';
import { setSaveSuccessModalVisible } from '../store/slices/editorUISlice';
import { useUiContext } from '../../../context/UiContext';
import SectionService from '../../../services/SectionService';
import { convertSectionsToTimeline } from '../lib/convertSections';
import {
  clearAssets,
  addAsset,
} from '../store/slices/assetsSlice';
import {
  clearTimeline,
  reorderItems,
  selectItem,
} from '../store/slices/timelineSlice';
import {
  reset as resetPlayback,
  seek,
} from '../store/slices/playbackSlice';
import { useDirtyHelpers } from './useUnsavedChanges';

/**
 * Hook for saving draft composition
 * @returns {Object} - { saveDraft, isSaving, saveProgress, saveProgressInfo }
 */
export default function useSaveDraft() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifyError } = useUiContext();
  const { markClean } = useDirtyHelpers();
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveProgressInfo, setSaveProgressInfo] = useState({
    sectionIndex: 0,
    totalSections: 0,
    operation: 'preparing',
  });

  const saveDraft = async ({
    title,
    folderId,
    meetingId,
    assets,
    timelineItems,
    userRole,
    userId,
    canUpdateDraftData = false,
    skipSuccessModal = false,
    skipRefetchAfterSave = false,
    pendingSaveItemIds,
  }) => {
    if (isSaving) {
      return undefined;
    }

    setIsSaving(true);
    setSaveProgress(0);
    setSaveProgressInfo({
      sectionIndex: 0,
      totalSections: 0,
      operation: 'preparing',
    });

    try {
      const meeting = await saveDraftComposition({
        title,
        folderId,
        assets,
        timelineItems,
        meetingId,
        userRole,
        userId,
        canUpdateDraftData,
        pendingSaveItemIds,
        onProgress: (sectionIndex, totalSections, progress, operation = 'processing') => {
          setSaveProgress(progress);
          setSaveProgressInfo({
            sectionIndex,
            totalSections,
            operation,
          });
        },
      });

      // Clear undo/redo history after successful save
      dispatch(clearHistory());
      markClean();

      setSaveProgress(100);

      // Refetch sections after successful save and update Redux state
      // This ensures all sections have sectionId and source_type: 'project'
      // Same approach as page load - fetch, convert, and replace Redux state
      if (meeting?.id && !skipRefetchAfterSave) {
        try {
          // Fetch all sections from backend
          const allSections = await SectionService.getSections(meeting.id);

          if (allSections && allSections.length > 0 && meeting.draftData?.sectionIds) {
            // Filter sections to only those in draftData.sectionIds (for draft mode)
            // Create a Set for efficient lookup
            const draftSectionIds = new Set(meeting.draftData.sectionIds);
            const draftSections = allSections.filter((section) => draftSectionIds.has(section.id));

            // Sort sections by their position in draftData.sectionIds array
            // ARCHITECTURAL PRINCIPLE: Draft mode uses only draftData.sectionIds array for ordering
            draftSections.sort((a, b) => {
              const indexA = meeting.draftData.sectionIds.indexOf(a.id);
              const indexB = meeting.draftData.sectionIds.indexOf(b.id);
              // If section not found in array, put it at the end
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });

            // Clear existing Redux state (same as page load)
            dispatch(clearAssets());
            dispatch(clearTimeline());
            dispatch(resetPlayback());

            // Convert sections to timeline format
            // Trim settings are now stored in section.trimSettings, so no need to pass trimMap
            const { assets: convertedAssets, timelineItems: convertedTimelineItems } = convertSectionsToTimeline(
              draftSections
            );

            // Add assets to Redux
            convertedAssets.forEach((asset) => {
              dispatch(addAsset(asset));
            });

            // Add timeline items to Redux in correct order
            // CRITICAL: Use reorderItems to preserve the exact order from draftData.sectionIds
            const reduxTimelineItems = convertedTimelineItems.map((item) => ({
              id: item.id,
              asset_id: item.asset_id,
              order: item.order, // Preserve order from convertSectionsToTimeline
              start_time: item.start_time,
              duration: item.duration,
              settings: {
                volume: 1,
                trim_start: item.settings.trim_start || 0,
                trim_end: item.settings.trim_end || 0,
              },
              metadata: item.metadata || {},
            }));

            // Set all items at once using reorderItems to preserve exact order
            dispatch(reorderItems(reduxTimelineItems));

            // Select first item and seek to beginning (same as page load)
            setTimeout(() => {
              if (reduxTimelineItems.length > 0) {
                dispatch(selectItem(reduxTimelineItems[0].id));
                dispatch(seek(0));
              }
            }, 0);
          }
        } catch (error) {
          // Log error but don't fail the save - sections were saved successfully
          // User can refresh page to see updated sections
          console.error('Failed to refetch sections after save:', error);
        }
      }
      
      // Skip showing success modal if requested (e.g., during publish flow)
      if (skipSuccessModal) {
        setIsSaving(false);
        return meeting; // Return meeting so it can be used in publish flow
      }
      
      // If this was a new meeting (no meetingId passed), redirect to edit mode
      // This ensures the URL has the meetingId and enables features like "Assign a section"
      if (!meetingId && meeting?.id) {
        navigate(`/my-videos/${meeting.id}/edit`, {
          state: { showSaveSuccessModal: true },
        });
      } else {
        // Show success modal instead of toast (only for existing meetings)
        dispatch(setSaveSuccessModalVisible(true));
      }
      
      setIsSaving(false);
      return meeting; // Return meeting for consistency
    } catch (error) {
      console.error('Error saving draft:', error);
      setSaveProgress(0);
      setIsSaving(false);
      // Check API response message first, then error.message, then default
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save draft';
      notifyError(errorMessage);
      throw error;
    }
  };

  return {
    saveDraft,
    isSaving,
    saveProgress,
    saveProgressInfo,
  };
}

